import React from "react";

import {
    Control,
    Controller,
    FieldPath,
    FieldValues,
    RegisterOptions,
} from "react-hook-form";

import styled from "@emotion/styled";

import ErrorMessage from "../../../../shared/forms/ReactHookInput/ErrorMessage";

interface iCheckboxProps<TFieldValues extends FieldValues> {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    placeholder?: string;
    children?: React.ReactNode;
    className?: string;
}

export const CheckboxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding-top: 1em;
    padding-bottom: 1em;
`;

const Row = styled.div`
    display: flex;
    gap: 1em;
    align-items: start;
    justify-content: start;
`;

const CheckboxBox = styled.div<{ hasError?: boolean }>`
    display: inline-flex;
    padding: 2px;
    border-radius: 4px;
    border: 2px solid
        ${({ hasError, theme }) =>
            hasError ? theme.colors.red.toString() : "transparent"};
`;

const StyledCheckbox = styled.input`
    min-width: 1.5em;
    min-height: 1.5em;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.blue.toString()};
`;

const Error = styled.span`
    font-size: small;
    color: ${({ theme }) => theme.colors.red.toString()};
`;

const Checkbox = <TFieldValues extends FieldValues>({
    name,
    control,
    rules = { required: false },
    children,
    placeholder,
    className,
}: iCheckboxProps<TFieldValues>) => {
    return (
        <Controller
            control={control}
            name={name}
            defaultValue={false}
            rules={rules}
            render={({ field, fieldState }) => (
                <div className={className}>
                    <Row>
                        <CheckboxBox hasError={!!fieldState.error}>
                            <StyledCheckbox
                                {...field}
                                id={name}
                                type="checkbox"
                                checked={field.value}
                                onChange={(e) => {
                                    field.onChange(e);
                                }}
                            />
                        </CheckboxBox>
                        {children && <label htmlFor={name}>{children}</label>}
                    </Row>
                    {fieldState.error && (
                        <ErrorMessage
                            error={fieldState.error}
                            name={placeholder ?? name}
                            rules={rules}
                        />
                    )}
                    {fieldState.error && (
                        <Error>{fieldState.error.message}</Error>
                    )}
                </div>
            )}
        />
    );
};

export default Checkbox;
